import { Box, Modal } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { closeModal } from 'utils/assets/images';
import { useUserProfileConfig } from 'store/users/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useDispatch } from 'react-redux';
import { handleGenerateDefinitionAI } from 'store/ebookNonNativeImageHighLighter/thunks';
import * as Sentry from '@sentry/react';

const ModalBox = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  outline: none;
`;

const ModalContent = styled.div`
  padding: 24px;
  position: relative;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const WordTextarea = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const GenerateAiButton = styled.button`
  padding: 8px 20px;
  background: #52c41a;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: #73d13d;
  }
`;

const SaveButton = styled.button`
  margin-top: 12px;
  padding: 8px 20px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: #40a9ff;
  }
`;

const TextareaContainer = styled.div`
  margin-bottom: 16px;
`;

const GeneratedTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #52c41a;
  border-radius: 5px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
  background-color: #ffffff;
`;

const DefinitionCard = styled.div`
  margin-bottom: 12px;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#52c41a' : '#e8e8e8'};
  border-radius: 8px;
  background-color: ${props => props.selected ? '#f6ffed' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${props => props.selected ? '#52c41a' : '#1890ff'};
    background-color: ${props => props.selected ? '#f6ffed' : '#f0f8ff'};
  }
`;

const DefinitionText = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
`;

const SelectButton = styled.button`
  padding: 8px 20px;
  background: ${props => props.selected ? '#52c41a' : '#1890ff'};
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: ${props => props.selected ? '#73d13d' : '#40a9ff'};
  }
`;

const AddDefinitionNonNativeAdmin = ({
  openDefinitionModal,
  handleClose,
  definitionText,
  definitionWord,
  getDefinition,
  modalZIndex = 1400,
  hideModalBackdrop = false,
}) => {
  const [userInput, setUserInput] = useState('');
  const [generatedDefinitions, setGeneratedDefinitions] = useState([]);
  const [selectedDefinitionIndex, setSelectedDefinitionIndex] = useState(null);
  const [manualDefinition, setManualDefinition] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const { isAiPermissionEnabled } = useUserProfileConfig();
  const showAlert = useAlert();
  const dispatch = useDispatch();

  useEffect(() => {
    if (openDefinitionModal) {
      setUserInput(definitionWord || '');
      setGeneratedDefinitions([]);
      setSelectedDefinitionIndex(null);
      setManualDefinition(definitionText || '');
    }
  }, [definitionText, definitionWord, openDefinitionModal]);

  const handleSave = () => {
    let finalText = '';
    if (manualDefinition.trim()) {
      finalText = manualDefinition;
    } else if (selectedDefinitionIndex !== null && generatedDefinitions[selectedDefinitionIndex]) {
      finalText = generatedDefinitions[selectedDefinitionIndex];
    } else if (userInput.trim()) {
      finalText = userInput;
    }
    
    if (finalText.trim()) {
      getDefinition(finalText?.trim() || null, userInput?.trim() || '');
      showAlert('Definition saved successfully!', 'success');
      handleClose();
    } else {
      showAlert('Please enter or generate a definition before saving.', 'warning');
    }
  };

  const handleSelectDefinition = (index) => {
    setSelectedDefinitionIndex(index);
    setManualDefinition(generatedDefinitions[index]);
  };

  const handleGenerateAi = async () => {
    if (!isAiPermissionEnabled) {
      showAlert('Please enable AI permission to generate definitions.', 'error');
      return;
    }

    if (!userInput || !userInput.trim()) {
      showAlert('Please enter a word to generate definitions.', 'warning');
      return;
    }

    setIsGenerating(true);
    setGeneratedDefinitions([]);
    setSelectedDefinitionIndex(null);
    
    try {
      const requestBody = {
        userInput: userInput.trim(),
      };

      const data = await dispatch(handleGenerateDefinitionAI(requestBody));
      if (data && data.error) {
        throw new Error(data.message || 'Failed to generate definition');
      }
      
      if (data && data.definitions && Array.isArray(data.definitions) && data.definitions.length > 0) {
        setGeneratedDefinitions(data.definitions);
        setSelectedDefinitionIndex(0);
        setManualDefinition(data.definitions[0]);
        showAlert(`Generated ${data.definitions.length} definition(s)! Please select one.`, 'success');
      } else {
        const generated = data?.definition || data?.text || data?.content || '';
        if (generated) {
          setGeneratedDefinitions([generated]);
          setSelectedDefinitionIndex(0);
          setManualDefinition(generated);
          showAlert('Definition generated successfully!', 'success');
        } else {
          showAlert('No definition was generated. Please try again.', 'warning');
        }
      }
    } catch (error) {
      Sentry.captureException(`Error generating definition: ${error}`);
      console.error('Error generating definition:', error);
      showAlert('Failed to generate definition. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal 
      open={openDefinitionModal} 
      onClose={handleClose}
      hideBackdrop={hideModalBackdrop}
      aria-labelledby="definition-modal-title"
      aria-describedby="definition-modal-description"
      sx={{ zIndex: modalZIndex, pointerEvents: openDefinitionModal ? 'auto' : 'none' }}
    >
      <ModalBox>
        <ModalContent>
          <CloseButton onClick={handleClose}>
            <img style={{ height: '24px', width: '24px' }} src={closeModal} alt="Close" />
          </CloseButton>
          
          <TextareaContainer>
            <Label>Word</Label>
            <WordTextarea
              placeholder="Type your Word here..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              disabled={isGenerating}
            />
          </TextareaContainer>

          <GenerateAiButton onClick={handleGenerateAi} disabled={isGenerating}>
            <AutoAwesome style={{ fontSize: 18 }} />
            {isGenerating ? 'Generating...' : 'Generate AI'}
          </GenerateAiButton>

          {generatedDefinitions.length > 0 && (
            <TextareaContainer>
              <Label>Select a Definition</Label>
              {generatedDefinitions.map((definition, index) => (
                <DefinitionCard
                  key={index}
                  selected={selectedDefinitionIndex === index}
                  onClick={() => handleSelectDefinition(index)}
                >
                  <DefinitionText>{definition}</DefinitionText>
                  <SelectButton selected={selectedDefinitionIndex === index}>
                    {selectedDefinitionIndex === index ? 'Selected' : 'Select'}
                  </SelectButton>
                </DefinitionCard>
              ))}
            </TextareaContainer>
          )}

          <TextareaContainer>
            <Label>Generated Definition (Editable)</Label>
            <GeneratedTextarea
              placeholder="Selected definition will appear here... You can edit it manually."
              value={manualDefinition}
              onChange={(e) => setManualDefinition(e.target.value)}
              readOnly={false}
            />
          </TextareaContainer>

          <SaveButton onClick={handleSave} disabled={isGenerating}>
            Save Definition
          </SaveButton>
        </ModalContent>
      </ModalBox>
    </Modal>
  );
};

export default AddDefinitionNonNativeAdmin;
